import { GeneralArrowLeftIcon } from '@outdoorsyco/bonfire';
import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { usePositionFixedOnScroll } from '@/hooks/usePositionFixedOnScroll';
import { hasPressed } from '@/utility/accessibility';

import css from './GalleryOverlay.module.css';

interface IGalleryOverlay {
  headerComponent?: React.ReactNode;
  categoriesComponent?: React.ReactNode;
  goToListing: () => void;
}

const GalleryOverlay: React.FC<React.PropsWithChildren<IGalleryOverlay>> = ({
  headerComponent,
  categoriesComponent,
  goToListing,
  children,
}) => {
  const intl = useIntl();
  const { position } = usePositionFixedOnScroll('gallery-header');

  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (hasPressed(e).ESCAPE) {
        goToListing();
      }
    },
    [goToListing],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      <header className={css.header} id="gallery-header" style={{ position }}>
        <div className={css.headerInner}>
          <button
            className={css.closeButton}
            aria-label={intl.formatMessage({ defaultMessage: 'Close gallery', id: '/q2ubh' })}
            data-testid="gallery-overlay-close-btn"
            onClick={goToListing}>
            <GeneralArrowLeftIcon className="w-6 h-6" />
          </button>
          <div className={css.headerContent}>{headerComponent}</div>
        </div>
      </header>
      <div className="grid grid-cols-main container-main gap-x-default">
        <section className="relative min-w-full gap-x-default col-start-0 col-span-16 lg:col-start-0 lg:col-span-18">
          <div className={`${css.content} md:gap-x-default md:grid-cols-main`}>
            <aside className={css.categories}>{categoriesComponent}</aside>
            <main className={css.photos}>{children}</main>
          </div>
        </section>
      </div>
    </>
  );
};

export default GalleryOverlay;
